import PropTypes from "prop-types";
import {
  Badge as MuiBadge,
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Tooltip,
} from "@mui/material";
import {
  Search as SearchIcon,
  OpenInNew,
  LinkOutlined,
  DeleteOutline,
} from "@mui/icons-material";
import { CheckCircle } from "lucide-react";
import { useConfirm } from "@accredo/fe-shared/contexts/ConfirmContext.jsx";
import Spinner from "@accredo/fe-shared/components/Spinner.jsx";
import TabList from "@accredo/fe-shared/components/TabList.jsx";
import { ListContainer, ListItem } from "./lists.jsx";
import { borderRadius, colorsFoundation } from "@accredo/fe-shared/theme/index.js";
import typography from "@accredo/fe-shared/theme/tokens/typography.js";
import {
  formatDatePeriod,
  getDatumZeit,
  getEuro,
} from "@accredo/fe-shared/services/formatService.js";

const PaymentMatchingPaymentsPanel = ({
  paymentImport,
  paymentsSearchword,
  onPaymentsSearchwordChange,
  paymentFilter,
  onPaymentFilterChange,
  filteredPayments = [],
  loadingPayments,
  selectedPayment,
  onSelectPayment,
  onAssignPaymentToExport,
  onOpenBookingExport,
  canViewBookingExport,
  getPaymentMatchCounts,
  handleOpenClaimDialog,
  approveAllExactMatches,
  hasUnassignedExactMatches,
  isAssigning,
  onIgnorePayment,
  onIgnoreRemaining,
  remainingCount = 0,
  hasRemainingMatches = false,
}) => {
  const confirm = useConfirm();
  const canIgnoreRemaining =
    paymentFilter === "unassigned" &&
    remainingCount > 0 &&
    !hasRemainingMatches &&
    paymentImport?.can_ignore_payments;

  const handleIgnorePayment = async (paymentId) => {
    const confirmed = await confirm({
      title: "Als nicht relevant markieren",
      message: "Zahlung als nicht relevant markieren?",
      confirmLabel: "Markieren",
    });
    if (confirmed) onIgnorePayment(paymentId);
  };

  const handleIgnoreRemaining = async () => {
    const confirmed = await confirm({
      title: "Als nicht relevant markieren",
      message: `${remainingCount} verbleibende Zahlungen als nicht relevant markieren?`,
      confirmLabel: "Markieren",
    });
    if (confirmed) onIgnoreRemaining();
  };

  const sortedPayments = [...filteredPayments].sort(
    (a, b) => new Date(b.processed_at) - new Date(a.processed_at)
  );

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: borderRadius.md,
        border: `1px solid ${colorsFoundation.neutral[200]}`,
        minHeight: 0,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          mb: 2,
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontSize: typography.size.xl, m: 0 }}
          gutterBottom
        >
          Zahlungen
        </Typography>
        {paymentImport && (
          <Typography variant="body2" color="text.secondary">
            <span>Zeitraum:</span>{" "}
            {formatDatePeriod(paymentImport.from_date, paymentImport.to_date, {
              format: "short",
            })}
          </Typography>
        )}
      </Box>
      <Box sx={{ mb: 2 }}>
        <TextField
          id="payment-matching-payment-search"
          placeholder="Zahlungen suchen..."
          value={paymentsSearchword}
          onChange={(event) => onPaymentsSearchwordChange(event.target.value)}
          fullWidth
          size="small"
          variant="outlined"
          label="Zahlung suchen"
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>
      <Box sx={{ mb: 2 }}>
        <TabList
          tabs={[
            { key: "all", label: "Alle" },
            { key: "unassigned", label: "Nicht zug." },
            { key: "assigned", label: "Zugeordnet" },
          ]}
          activeTab={paymentFilter}
          onTabChange={onPaymentFilterChange}
          containerSx={{
            mt: 0,
            borderBottom: `1px solid ${colorsFoundation.neutral[200]}`,
          }}
        />
      </Box>
      {(hasUnassignedExactMatches || canIgnoreRemaining) && (
        <Box sx={{ mb: 2, display: "flex", flexDirection: "column", gap: 1 }}>
          {hasUnassignedExactMatches && (
          <Button
            variant="contained"
            color="success"
            size="medium"
            fullWidth
            onClick={approveAllExactMatches}
            disabled={isAssigning}
            startIcon={<CheckCircle size={16} />}
          >
            {isAssigning ? "Wird zugeordnet..." : "Exakte Zahlungen zuordnen"}
          </Button>
          )}
          {canIgnoreRemaining && (
            <Button
              variant="outlined"
              size="medium"
              fullWidth
              onClick={handleIgnoreRemaining}
              disabled={isAssigning}
              startIcon={<DeleteOutline fontSize="small" />}
            >
              Alle verbleibenden als nicht relevant markieren
            </Button>
          )}
        </Box>
      )}
      <Box sx={{ flex: 1, overflow: "auto" }}>
        {loadingPayments ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <Spinner size={24} />
          </Box>
        ) : (
          <ListContainer>
            {sortedPayments.map((payment) => {
              // Eine Zahlung gilt auch als zugeordnet, wenn sie einem Booking-
              // Export zugewiesen wurde (Auszahlungen, die automatisch beim
              // Import gegen bestehende Buchungen verrechnet wurden).
              const isAssigned =
                Boolean(payment.booking_id) || Boolean(payment.booking_export_id);
              const isSelectable = !isAssigned && payment.debit_credit === "C";
              // Nicht zugeordnete Auszahlungen werden über einen Booking-Export
              // zugeordnet statt über den Schaden-Workflow (Schritt 2/3).
              const isExportAssignable =
                !isAssigned && payment.debit_credit !== "C";
              const isSelected = selectedPayment?.id === payment.id;
              const matchCounts = getPaymentMatchCounts(payment.id);

              return (
                <ListItem
                  key={payment.id}
                  selected={isSelected}
                  onClick={
                    isSelectable
                      ? () => onSelectPayment(payment)
                      : isExportAssignable
                        ? () => onAssignPaymentToExport(payment)
                        : undefined
                  }
                  showSelectionIndicator
                  sx={{
                    backgroundColor: isAssigned
                      ? colorsFoundation.status.positive[50]
                      : "inherit",
                    borderLeft: isAssigned
                      ? `4px solid ${colorsFoundation.status.positive[500]}`
                      : "4px solid transparent",
                    cursor:
                      isSelectable || isExportAssignable ? "pointer" : "default",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 1,
                      }}
                    >
                      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
                        {isAssigned && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Box
                              component="span"
                              sx={{
                                px: 1,
                                py: 0.25,
                                borderRadius: 999,
                                fontSize: "0.75rem",
                                backgroundColor:
                                  colorsFoundation.status.positive[100],
                                color: colorsFoundation.status.positive[700],
                              }}
                            >
                              {payment.booking?.schaden?.schaden_nummer_accredo ||
                                "Zugeordnet"}
                            </Box>
                            {payment.booking_export_id && (
                              <Box
                                component="span"
                                sx={{
                                  px: 1,
                                  py: 0.25,
                                  borderRadius: 999,
                                  fontSize: "0.75rem",
                                  backgroundColor:
                                    colorsFoundation.status.informative[100],
                                  color: colorsFoundation.status.informative[700],
                                }}
                              >
                                Export
                              </Box>
                            )}
                            {payment.booking?.schaden_id && (
                              <IconButton
                                size="small"
                                aria-label="Schadendetails anzeigen"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleOpenClaimDialog(
                                    payment.booking.schaden_id
                                  );
                                }}
                              >
                                <OpenInNew fontSize="small" />
                              </IconButton>
                            )}
                            {payment.booking_export_id && canViewBookingExport && (
                              <IconButton
                                size="small"
                                aria-label="Buchungsexport öffnen"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  onOpenBookingExport(payment.booking_export_id);
                                }}
                              >
                                <OpenInNew fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                        )}
                        <Typography variant="caption" color="text.secondary">
                          {payment.remitter}
                        </Typography>
                        <Typography variant="body1">
                          {payment.description}
                        </Typography>
                      </Box>

                      {!isAssigned &&
                        (matchCounts.exact > 0 || matchCounts.possible > 0) && (
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 0.5,
                              alignItems: "flex-end",
                            }}
                          >
                            {matchCounts.exact > 0 && (
                              <MuiBadge
                                badgeContent={matchCounts.exact}
                                overlap="circular"
                                sx={{
                                  "& .MuiBadge-badge": {
                                    backgroundColor: colorsFoundation.key.blue[700],
                                    color: colorsFoundation.neutral.white,
                                  },
                                }}
                              >
                                <Box sx={{ width: 24, height: 24 }} />
                              </MuiBadge>
                            )}
                            {matchCounts.possible > 0 && (
                              <MuiBadge
                                badgeContent={matchCounts.possible}
                                overlap="circular"
                                sx={{
                                  "& .MuiBadge-badge": {
                                    backgroundColor:
                                      colorsFoundation.status.critical[400],
                                    color: colorsFoundation.neutral[900],
                                  },
                                }}
                              >
                                <Box sx={{ width: 24, height: 24 }} />
                              </MuiBadge>
                            )}
                          </Box>
                        )}

                      {!isAssigned &&
                        payment.status === "imported" &&
                        matchCounts.exact === 0 &&
                        matchCounts.possible === 0 &&
                                              payment.can_ignore && (
                          <Tooltip title="Als nicht relevant markieren">
                            <IconButton
                              size="small"
                              aria-label="Als nicht relevant markieren"
                              sx={{ alignSelf: "flex-start" }}
                              onClick={(event) => {
                                event.stopPropagation();
                                handleIgnorePayment(payment.id);
                              }}
                            >
                              <DeleteOutline fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mt: 1,
                        alignItems: "center",
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Datum: {getDatumZeit(payment.processed_at)}
                      </Typography>
                      <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color:
                              payment.debit_credit === "C"
                                ? "green"
                                : "error.main",
                          }}
                        >
                          {payment.debit_credit === "C" ? "" : "-"}
                          {getEuro(payment.amount)}
                        </Typography>
                    </Box>
                  </Box>
                </ListItem>
              );
            })}

            {sortedPayments.length === 0 && (
              <Box sx={{ textAlign: "center", py: 8 }}>
                <Typography variant="body1" color="text.secondary">
                  Keine offenen Zahlungen vorhanden
                </Typography>
              </Box>
            )}
          </ListContainer>
        )}
      </Box>
    </Paper>
  );
};

PaymentMatchingPaymentsPanel.propTypes = {
  paymentImport: PropTypes.shape({
    from_date: PropTypes.string,
    to_date: PropTypes.string,
    can_ignore_payments: PropTypes.bool,
  }),
  paymentsSearchword: PropTypes.string.isRequired,
  onPaymentsSearchwordChange: PropTypes.func.isRequired,
  paymentFilter: PropTypes.string.isRequired,
  onPaymentFilterChange: PropTypes.func.isRequired,
  filteredPayments: PropTypes.arrayOf(PropTypes.object),
  loadingPayments: PropTypes.bool.isRequired,
  selectedPayment: PropTypes.object,
  onSelectPayment: PropTypes.func.isRequired,
  onAssignPaymentToExport: PropTypes.func.isRequired,
  onOpenBookingExport: PropTypes.func.isRequired,
  canViewBookingExport: PropTypes.bool,
  getPaymentMatchCounts: PropTypes.func.isRequired,
  handleOpenClaimDialog: PropTypes.func.isRequired,
  approveAllExactMatches: PropTypes.func.isRequired,
  hasUnassignedExactMatches: PropTypes.bool,
  isAssigning: PropTypes.bool.isRequired,
  onIgnorePayment: PropTypes.func.isRequired,
  onIgnoreRemaining: PropTypes.func.isRequired,
  remainingCount: PropTypes.number,
  hasRemainingMatches: PropTypes.bool,
};

export default PaymentMatchingPaymentsPanel;
